import {
  ADMIN_TIME_ZONE,
  formatAdminDate,
  formatCalendarDate,
} from "@/lib/format/admin-time";

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "2-digit",
};

/** Relative age of a MOMENT (createdAt, lastActiveAt). */
export function formatRelative(
  iso: string | null | undefined,
  emptyLabel = "Never",
): string {
  if (!iso) return emptyLabel;
  const diff = Date.now() - new Date(iso).getTime();

  if (diff < MIN) return "just now";
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} hr ago`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)} d ago`;
  return formatShortDate(iso);
}

/** A MOMENT's day on the admins' (IST) clock, e.g. "29 Sept 26". */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    ...SHORT_DATE,
    timeZone: ADMIN_TIME_ZONE,
  });
}

/** A CALENDAR date (`@db.Date`, stored as 00:00Z) exactly as stored. */
export function formatShortCalendarDate(
  iso: Date | string | null | undefined,
): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    ...SHORT_DATE,
    timeZone: "UTC",
  });
}

/**
 * Age of a CALENDAR date in whole days against the admins' today —
 * "today", "yesterday", "N d ago", then the date. A date has no clock
 * time, so it is never "3 hr ago".
 */
export function formatCalendarAgo(
  iso: Date | string | null | undefined,
  now: Date = new Date(),
  emptyLabel = "Never",
): string {
  if (!iso) return emptyLabel;
  const day = new Date(`${formatCalendarDate(iso)}T00:00:00.000Z`).getTime();
  const today = new Date(`${formatAdminDate(now)}T00:00:00.000Z`).getTime();
  const days = Math.round((today - day) / DAY);

  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} d ago`;
  return formatShortCalendarDate(iso);
}
